import React, {FC, useCallback, useEffect, useState} from 'react'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {createLocation, deleteLocation, loadLocations, updateLocation} from './Api'
import {ILocationModel} from './LocationModel'
import {LocationTable} from './LocationTable'
import {LocationFormModal} from './LocationFormModal'

export const HR_LOCATION_PATH = '/hr/location'

const LocationPage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [locations, setLocations] = useState<ILocationModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState<ILocationModel | null>(null)
  const [deletingItem, setDeletingItem] = useState<ILocationModel | null>(null)

  const fetchLocations = useCallback(async (page: number) => {
    setLoading(true)
    try {
      const payload = (await loadLocations(page)).data.payload
      setLocations(payload.content)
      setTotalPages(payload.totalPages)
    } catch (error) {
      showErrorToast('Lokasyonlar yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLocations(0)
  }, [fetchLocations])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchLocations(newPage)
      }
    },
    [totalPages, fetchLocations]
  )

  const handleCreateClick = () => {
    setEditingItem(null)
    setShowForm(true)
  }

  const handleEdit = useCallback((location: ILocationModel) => {
    setEditingItem(location)
    setShowForm(true)
  }, [])

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingItem(null)
  }

  const handleSubmit = async (location: ILocationModel) => {
    setSaving(true)
    try {
      if (editingItem?.id) {
        await updateLocation({...location, id: editingItem.id})
        showSuccessToast('Lokasyon başarıyla güncellendi')
        await fetchLocations(currentPage)
      } else {
        await createLocation({...location, id: undefined})
        showSuccessToast('Lokasyon başarıyla oluşturuldu')
        setCurrentPage(0)
        await fetchLocations(0)
      }
      handleCloseForm()
    } catch (error) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingItem?.id) return
    setLoading(true)
    try {
      await deleteLocation(deletingItem.id)
      showSuccessToast('Lokasyon başarıyla silindi')
      await fetchLocations(currentPage)
    } catch (error) {
      showErrorToast('Lokasyon silinirken hata oluştu')
    } finally {
      setDeletingItem(null)
      setLoading(false)
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Lokasyon Tanımı</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Lokasyonlar</span>
            <span className='text-muted mt-1 fw-bold fs-7'>İş ilanlarında kullanılan il / ilçe tanımları</span>
          </h3>
          <div className='card-toolbar'>
            <button type='button' className='btn btn-sm btn-primary' onClick={handleCreateClick}>
              <i className='bi bi-plus-lg fs-5'></i>
              Yeni Kayıt
            </button>
          </div>
        </div>
        <div className='card-body pt-0'>
          <LocationTable
            data={locations}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onDelete={setDeletingItem}
            loading={loading}
          />
        </div>
      </div>

      <LocationFormModal
        show={showForm}
        editingItem={editingItem}
        loading={saving}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        show={!!deletingItem}
        header='Silme Onayı'
        text={`"${deletingItem?.province ?? ''} / ${deletingItem?.district ?? ''}" lokasyonunu silmek istediğinize emin misiniz?`}
        handleClose={() => setDeletingItem(null)}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  )
}

export {LocationPage}
